import {QRCodeSVG} from 'qrcode.react'
import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '../../../shared/config/toastConfig'
import {getLogger} from '../../../shared/utils/logUtils'
import {useCognitoAuth} from '../auth/core/CognitoAuth'
import {
  buildOtpAuthUri,
  disableTotpMfa,
  resolveTotpMfaEnabled,
  startTotpSetup,
  verifyAndEnableTotp,
} from '../auth/core/totpMfa'

const TOTP_CODE_LENGTH = 6

/** Same rule as the SMS modal: federated Google users have a `Google_<sub>` username. */
function isGoogleFederatedPrimaryUsername(username: string): boolean {
  return /^google_/i.test(username)
}

/** Groups the secret in blocks of 4 so it is easier to type into an app by hand. */
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

export type MfaTotpSettingsModalProps = {
  show: boolean
  onHide: () => void
  onStatusChanged: () => void
}

export const MfaTotpSettingsModal: React.FC<MfaTotpSettingsModalProps> = ({
  show,
  onHide,
  onStatusChanged,
}) => {
  const {cognitoAuth, currentUser} = useCognitoAuth()
  const [loading, setLoading] = useState(false)
  // null while loading or when the lookup failed; actions stay hidden until it is known.
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null)
  const [statusFailed, setStatusFailed] = useState(false)
  const [step, setStep] = useState<'choose' | 'scan'>('choose')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')

  const username = cognitoAuth?.getUsername?.() ?? ''
  const isGoogle = isGoogleFederatedPrimaryUsername(username)

  useEffect(() => {
    if (!show || !cognitoAuth) {
      return
    }
    let cancelled = false
    void resolveTotpMfaEnabled(cognitoAuth).then((enabled) => {
      if (!cancelled) {
        setTotpEnabled(enabled)
        setStatusFailed(enabled === null)
      }
    })
    return () => {
      cancelled = true
    }
  }, [show, cognitoAuth])

  useEffect(() => {
    if (!show) {
      setTotpEnabled(null)
      setStatusFailed(false)
      setStep('choose')
      setSecret('')
      setCode('')
    }
  }, [show])

  const handleStart = async () => {
    if (!cognitoAuth) {
      return
    }
    setLoading(true)
    try {
      setSecret(await startTotpSetup(cognitoAuth))
      setStep('scan')
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Could not start authenticator setup', {
        ...toastDefaultConfig,
        type: 'error',
      })
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!cognitoAuth || code.length !== TOTP_CODE_LENGTH) {
      return
    }
    setLoading(true)
    try {
      await verifyAndEnableTotp(cognitoAuth, code)
      toast('Authenticator app two-factor authentication is enabled.', {
        ...toastDefaultConfig,
        type: 'success',
      })
      setTotpEnabled(true)
      onStatusChanged()
      onHide()
    } catch (e: unknown) {
      toast(
        e instanceof Error && e.message ? e.message : 'Invalid code. Check the time on your phone.',
        {...toastDefaultConfig, type: 'error'}
      )
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDisable = async () => {
    if (!cognitoAuth) {
      return
    }
    setLoading(true)
    try {
      await disableTotpMfa(cognitoAuth)
      toast('Authenticator app two-factor authentication is turned off.', {
        ...toastDefaultConfig,
        type: 'info',
      })
      setTotpEnabled(false)
      onStatusChanged()
      onHide()
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Could not disable MFA', {
        ...toastDefaultConfig,
        type: 'error',
      })
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  const accountName = currentUser?.email || username

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName='ob-dialog'>
      <Modal.Header closeButton>
        <Modal.Title>Authenticator app two-factor authentication</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!cognitoAuth && (
          <p className='text-gray-600'>Sign in to manage two-factor authentication.</p>
        )}
        {cognitoAuth && isGoogle && (
          <p className='text-gray-600'>
            This account signs in with Google only. Google handles authentication for that path, so
            Cognito two-factor authentication for email and password does not apply here.
          </p>
        )}
        {cognitoAuth && !isGoogle && statusFailed && (
          <p className='text-danger'>
            Could not load your two-factor settings. Close this window and try again.
          </p>
        )}
        {cognitoAuth && !isGoogle && totpEnabled === true && step === 'choose' && (
          <div>
            <p className='mb-4'>An authenticator app is currently enabled for your account.</p>
            <button
              type='button'
              className='btn btn-light-danger'
              disabled={loading}
              onClick={() => void handleDisable()}
            >
              Turn off authenticator app
            </button>
          </div>
        )}
        {cognitoAuth && !isGoogle && totpEnabled === false && step === 'choose' && (
          <div>
            <p className='text-gray-600 mb-4'>
              Use an app such as Google Authenticator, Microsoft Authenticator or 1Password. After
              you enable this, you enter a code from the app each time you sign in with email and
              password.
            </p>
            <button
              type='button'
              className='btn btn-primary'
              disabled={loading}
              onClick={() => void handleStart()}
            >
              Set up authenticator app
            </button>
          </div>
        )}
        {cognitoAuth && !isGoogle && step === 'scan' && secret && (
          <div>
            <p className='text-gray-600'>1. Scan this QR code with your authenticator app.</p>
            <div className='d-flex justify-content-center bg-white p-4 mb-3'>
              <QRCodeSVG
                value={buildOtpAuthUri(secret, accountName)}
                size={180}
                role='img'
                aria-label='Authenticator setup QR code'
              />
            </div>
            <p className='text-gray-600 fs-7 mb-1'>Can't scan it? Enter this key instead:</p>
            <code className='d-block mb-4 user-select-all' data-testid='totp-secret'>
              {formatSecret(secret)}
            </code>
            <label className='form-label fw-bold' htmlFor='mfa-totp-code'>
              2. Enter the 6-digit code from the app
            </label>
            <input
              id='mfa-totp-code'
              className='form-control form-control-solid mb-3'
              inputMode='numeric'
              autoComplete='one-time-code'
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleVerify()
                }
              }}
            />
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-light'
                disabled={loading}
                onClick={() => setStep('choose')}
              >
                Back
              </button>
              <button
                type='button'
                className='btn btn-primary'
                disabled={loading || code.length !== TOTP_CODE_LENGTH}
                onClick={() => void handleVerify()}
              >
                Verify and enable
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
